import React, { useRef, useLayoutEffect } from 'react';
import { gsap, ScrollTrigger } from '../../lib/gsapSetup';
import { useTranslation } from '../../i18n/LanguageContext';
import playstoreIcon from '../../assets/playstore.png';
import appStoreIcon from '../../assets/app-store.png';

const bgImage = '/parallax-bg.webp';
const truckImage = '/parallax-truck.webp';

/**
 * HERO — 3-frame cinematic parallax scroll.
 *
 * FRAME 1: Road bg with heavy fog/overlay. Text centered. CTA button. No truck.
 * FRAME 2: Overlay lightens. Truck rises from bottom, grows HUGE, fills center. Text gone.
 * FRAME 3: Truck slides right (partially off-screen). Text fades in from left. Road visible.
 *
 * Pinned for 400vh. Smooth transitions with breathing room between frames.
 */
export default function Hero() {
  const sectionRef = useRef(null);
  const pinWrapRef = useRef(null);
  const bgRef = useRef(null);
  const overlayRef = useRef(null);
  const truckRef = useRef(null);
  const frame1Ref = useRef(null);
  const frame3Ref = useRef(null);
  const scrollIndRef = useRef(null);
  const { t, lang } = useTranslation();

  useLayoutEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const rafId = requestAnimationFrame(() => {
      const ctx = gsap.context(() => {
        const smooth = 'power3.inOut';

        /* ── ENTRY ANIMATION (page load) ── */
        const entry = gsap.timeline({ defaults: { ease: 'power4.out' } });

        entry.fromTo(
          bgRef.current,
          { scale: 1.2, opacity: 0 },
          { scale: 1, opacity: 1, duration: 1.8, ease: 'power2.out' },
          0,
        );
        entry.fromTo(
          overlayRef.current,
          { opacity: 0 },
          { opacity: 1, duration: 1.2 },
          0.2,
        );
        // Text + CTA rise from bottom
        entry.fromTo(
          frame1Ref.current,
          { opacity: 0, y: 100 },
          { opacity: 1, y: 0, duration: 1.4, ease: 'power3.out' },
          0.5,
        );
        entry.fromTo(
          scrollIndRef.current,
          { opacity: 0 },
          { opacity: 1, duration: 0.5 },
          1.6,
        );

        /* ── SCROLL TIMELINE ──
         *
         *  0   – 0.5  HOLD Frame 1
         *  0.5 – 2.0  Transition 1→2 (text out, truck in from bottom to center)
         *  2.0 – 3.5  HOLD Frame 2 (truck big, centered)
         *  3.5 – 5.5  Transition 2→3 (truck slides right, text in from left)
         *  5.5 – 7.0  HOLD Frame 3
         */
        const master = gsap.timeline({
          scrollTrigger: {
            trigger: section,
            start: 'top top',
            end: '+=400%',
            scrub: 0.6,
            pin: pinWrapRef.current,
            anticipatePin: 1,
            invalidateOnRefresh: true,
          },
        });

        // ─── Background: slow upward parallax drift throughout ───
        master.to(
          bgRef.current,
          { yPercent: -10, scale: 1.1, ease: 'none', duration: 7 },
          0,
        );

        // ─── Overlay: starts heavy (frame 1), lightens for frame 2, re-darkens for frame 3 ───
        // Frame 1 → 2: lighten
        master.to(
          overlayRef.current,
          { opacity: 0.4, duration: 1.5, ease: smooth },
          0.5,
        );
        // Frame 2 → 3: darken again
        master.to(
          overlayRef.current,
          { opacity: 0.85, duration: 1.5, ease: smooth },
          3.5,
        );

        // ─── Scroll indicator fades ───
        master.to(
          scrollIndRef.current,
          { opacity: 0, y: -15, duration: 0.4, ease: smooth },
          0.1,
        );

        /* ── Frame 1 → 2 ── */

        // Text + CTA fade up and disappear
        master.fromTo(
          frame1Ref.current,
          { opacity: 1, y: 0 },
          { opacity: 0, y: -100, duration: 1.2, ease: smooth },
          0.5,
        );

        // Truck: starts below viewport center, rises to center of viewport (Frame 2)
        master.fromTo(
          truckRef.current,
          {
            top: '50%',
            left: '50%',
            xPercent: -50,
            yPercent: 120,
            opacity: 0,
            scale: 0.7,
          },
          {
            top: '50%',
            left: '50%',
            xPercent: -50,
            yPercent: -50,
            opacity: 1,
            scale: 1,
            duration: 1.5,
            ease: smooth,
          },
          0.7,
        );

        /* ── Frame 2 → 3 ── */

        const isMobile = window.innerWidth < 768;

        if (isMobile) {
          // On mobile: fade truck out during transition to Frame 3
          master.to(
            truckRef.current,
            { opacity: 0, scale: 0.5, duration: 1.5, ease: smooth },
            3.5,
          );
        } else {
          // Desktop: truck slides from center to final position
          // top:0%, left:10%, translate(45%, 15%) scale(0.8)
          master.to(
            truckRef.current,
            {
              top: '0%',
              left: '10%',
              xPercent: 45,
              yPercent: 15,
              scale: 0.8,
              duration: 2,
              ease: smooth,
            },
            3.5,
          );
        }

        // Frame 3 text fades in from the left
        master.fromTo(
          frame3Ref.current,
          { opacity: 0, x: -60 },
          { opacity: 1, x: 0, duration: 1.5, ease: smooth },
          4.0,
        );

        ScrollTrigger.refresh();
      }, section);

      sectionRef._gsapCtx = ctx;
    });

    return () => {
      cancelAnimationFrame(rafId);
      if (sectionRef._gsapCtx) {
        sectionRef._gsapCtx.revert();
        sectionRef._gsapCtx = null;
      }
    };
  }, [lang]);

  const scrollDown = () => {
    const next = document.getElementById('features');
    if (next) {
      const top = next.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  return (
    <section
      ref={sectionRef}
      id='hero'
      dir='ltr'
      className='relative bg-[#0A174E]'
    >
      <div
        ref={pinWrapRef}
        className='relative h-screen w-full overflow-hidden'
      >
        {/* === BACKGROUND === */}
        <img
          ref={bgRef}
          src={bgImage}
          alt=''
          aria-hidden='true'
          className='absolute inset-0 w-full h-full object-cover will-change-transform'
          style={{ opacity: 0, transform: 'scale(1.2)' }}
        />

        {/* === OVERLAY (heavy grey fog for frame 1) === */}
        <div
          ref={overlayRef}
          className='absolute inset-0 z-[1]'
          style={{
            background:
              'linear-gradient(180deg, rgba(60,60,60,0.7) 0%, rgba(40,40,40,0.55) 40%, rgba(0,0,0,0.45) 100%)',
            opacity: 0,
          }}
        />

        {/* === TRUCK ===
             Positioned at center of viewport via top:50%/left:50% + translate(-50%,-50%).
             Width ~80vw so it fills the screen in Frame 2.
             GSAP uses xPercent/yPercent relative to the element's own size.
        */}
        <div
          ref={truckRef}
          className='absolute z-[5] will-change-transform pointer-events-none'
          style={{
            top: '50%',
            left: '50%',
            width: 'clamp(500px, 80vw, 1100px)',
            opacity: 0,
          }}
        >
          <img
            src={truckImage}
            alt='LinxExpress truck'
            className='w-full h-auto drop-shadow-[0_20px_80px_rgba(0,0,0,0.6)]'
          />
        </div>

        {/* === FRAME 1: Centered text + CTA === */}
        <div
          ref={frame1Ref}
          className='absolute inset-0 z-10 flex flex-col items-center justify-center px-5 sm:px-6 lg:px-8 text-center'
          style={{ opacity: 0 }}
        >
          <h1
            className='font-display text-[clamp(2.2rem,5vw,4.5rem)] font-bold text-white leading-[1.1] tracking-[-0.03em] max-w-4xl mx-auto mb-5'
            style={{ textShadow: '0 2px 8px rgba(0,0,0,0.9), 0 4px 40px rgba(0,0,0,0.8)' }}
          >
            {t('hero.headline')}
          </h1>
          <p className='text-base sm:text-lg lg:text-xl text-white/70 font-light leading-relaxed max-w-xl mx-auto'
            style={{ textShadow: '0 2px 12px rgba(0,0,0,0.9), 0 4px 30px rgba(0,0,0,0.7)' }}
          >
            {t('hero.subtitle')}
          </p>
        </div>

        {/* === FRAME 3: Left-aligned text (truck is right) === */}
        <div
          ref={frame3Ref}
          className='absolute inset-0 z-10 flex items-center px-8 sm:px-12 lg:px-16 xl:px-24'
          style={{ opacity: 0 }}
        >
          <div className='max-w-xl'>
            <h2
              className='font-display text-[clamp(2rem,4.5vw,4.5rem)] font-extrabold text-white leading-[1.08] tracking-[-0.02em] mb-5'
              style={{ textShadow: '0 2px 8px rgba(0,0,0,0.9), 0 4px 40px rgba(0,0,0,0.8)' }}
            >
              {t('hero.finalHeadline')}
            </h2>
            <p
              className='text-sm sm:text-base lg:text-lg text-white/55 font-light leading-relaxed max-w-md mb-8'
              style={{ textShadow: '0 2px 12px rgba(0,0,0,0.9), 0 4px 30px rgba(0,0,0,0.7)' }}
            >
              {t('hero.finalSubtitle')}
            </p>

            {/* Download buttons */}
            <div className='flex flex-wrap gap-3'>
              {/* Google Play */}
              <a
                href='https://play.google.com/store/apps/details?id=com.linxexpress'
                target='_blank'
                rel='noopener noreferrer'
                className='inline-flex items-center gap-3 px-5 py-3 rounded-xl bg-white/10 hover:bg-white/20 border border-white/20 hover:border-white/40 text-white transition-all duration-300 backdrop-blur-sm'
              >
                <img src={playstoreIcon} alt='Google Play' className='w-6 h-6 flex-shrink-0' />
                <div className='text-left leading-none'>
                  <div className='text-[10px] text-white/60 uppercase tracking-widest mb-0.5'>
                    {t('download.googlePlay.label')}
                  </div>
                  <div className='text-sm font-semibold'>Google Play</div>
                </div>
              </a>

              {/* App Store */}
              <a
                href='https://apps.apple.com/app/linxexpress'
                target='_blank'
                rel='noopener noreferrer'
                className='inline-flex items-center gap-3 px-5 py-3 rounded-xl bg-white/10 hover:bg-white/20 border border-white/20 hover:border-white/40 text-white transition-all duration-300 backdrop-blur-sm'
              >
                <img src={appStoreIcon} alt='App Store' className='w-6 h-6 flex-shrink-0' />
                <div className='text-left leading-none'>
                  <div className='text-[10px] text-white/60 uppercase tracking-widest mb-0.5'>
                    {t('download.appStore.label')}
                  </div>
                  <div className='text-sm font-semibold'>App Store</div>
                </div>
              </a>
            </div>
          </div>
        </div>

        {/* === SCROLL INDICATOR === */}
        <div
          ref={scrollIndRef}
          className='absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-3 cursor-pointer'
          onClick={scrollDown}
          role='button'
          tabIndex={0}
          aria-label={t('hero.scrollDown')}
          style={{ opacity: 0 }}
        >
          <span className='text-[11px] uppercase tracking-[0.25em] text-white/30 font-medium'>
            {t('hero.scroll')}
          </span>
          <div className='w-[1px] h-10 bg-gradient-to-b from-white/30 to-transparent relative overflow-hidden'>
            <div className='absolute top-0 left-0 w-full h-3 bg-white/80 animate-scroll-pulse' />
          </div>
        </div>
      </div>
    </section>
  );
}
